<template>
  <div
    ref="trackRef"
    class="relative h-[30px] bg-[#d4d8e1]/25 overflow-hidden select-none cursor-pointer rounded-[3px]"
    @pointerdown="onTrackPointerDown"
  >
    <!-- Thumbnail strip -->
    <div class="absolute inset-0 flex">
      <img
        v-for="(frameUrl, i) in displayFrames"
        :key="i"
        :src="frameUrl"
        class="h-full object-cover shrink-0"
        :style="{ width: `${100 / displayFrames.length}%` }"
        draggable="false"
      />
    </div>

    <!-- Dimming overlay (faded everywhere) -->
    <div class="absolute inset-0 bg-white/85 pointer-events-none" />
    <div class="absolute inset-0 bg-[#afb5c0]/25 pointer-events-none" />

    <!-- Miniature match highlights (under viewport) -->
    <div class="absolute inset-0 pointer-events-none">
      <div
        v-for="m in matches"
        :key="m.id"
        class="absolute top-0 bottom-0 rounded-[3px] border-l-2 transition-opacity duration-150"
        :style="highlightStyle(m)"
      />
    </div>

    <!-- Viewport window (solid black border, transparent middle) -->
    <div
      class="absolute inset-y-0 border-2 border-black rounded-[3px] pointer-events-auto cursor-grab"
      :class="{ 'cursor-grabbing': isDragging === 'window' }"
      :style="windowStyle"
      @pointerdown.stop="onWindowPointerDown"
    />
  </div>
</template>

<script setup lang="ts">
import { ScoreLevel } from '@/shared/graphql/router/graphql'
import { computed, inject, onUnmounted, ref } from 'vue'
import { TimelineHoverKey, type TimelineSideMatch } from './timeline-types'

const props = defineProps<{
  frames?: readonly string[]
  matches: readonly TimelineSideMatch[]
  duration: number
  /** Current horizontal scroll position of the main timeline, in pixels */
  scrollLeft: number
  /** Full scrollable content width of the main timeline, in pixels */
  contentWidth: number
  /** Visible viewport width (the overflow container), in pixels */
  viewportWidth: number
}>()

const emit = defineEmits<{
  'update:scrollLeft': [value: number]
}>()

const MAX_THUMBS = 25

const displayFrames = computed(() => {
  const all = props.frames ?? []
  if (!all.length) return []
  if (all.length <= MAX_THUMBS) return [...all]
  const out: string[] = []
  for (let i = 0; i < MAX_THUMBS; i++) {
    const idx = Math.round((i * (all.length - 1)) / (MAX_THUMBS - 1))
    out.push(all[idx])
  }
  return out
})

const HIGHLIGHT_PALETTE: Record<ScoreLevel, { bg: string; border: string }> = {
  [ScoreLevel.High]: { bg: 'rgba(220, 60, 63, 0.15)', border: '#dc3c3f' },
  [ScoreLevel.Medium]: { bg: 'rgba(245, 185, 88, 0.15)', border: '#f5b958' },
  [ScoreLevel.Low]: { bg: 'rgba(52, 177, 199, 0.15)', border: '#34b1c7' },
}

const hover = inject(TimelineHoverKey, null)

const highlightStyle = (m: TimelineSideMatch) => {
  const dur = props.duration || 1
  const start = Math.max(0, (m.start / dur) * 100)
  const end = Math.min(100, (m.end / dur) * 100)
  const colors =
    HIGHLIGHT_PALETTE[m.level] ?? HIGHLIGHT_PALETTE[ScoreLevel.High]
  const id = hover?.hoveredId.value
  const dimmed = id && id !== m.id
  return {
    left: `${start}%`,
    width: `${Math.max(0, end - start)}%`,
    backgroundColor: colors.bg,
    borderColor: colors.border,
    opacity: dimmed ? 0.25 : 1,
  }
}

const windowStyle = computed(() => {
  const total = props.contentWidth || 1
  const pct = Math.max(0, Math.min(1, props.viewportWidth / total))
  const left = Math.max(
    0,
    Math.min(1 - pct, props.scrollLeft / total)
  )
  return {
    left: `${left * 100}%`,
    width: `${pct * 100}%`,
  }
})

const trackRef = ref<HTMLElement>()
const isDragging = ref<'track' | 'window' | null>(null)
let dragOffset = 0

const scrollFromPointer = (clientX: number, offsetPx: number) => {
  const el = trackRef.value
  if (!el) return
  const rect = el.getBoundingClientRect()
  const targetLeftPx = clientX - rect.left - offsetPx
  const trackPx = rect.width
  const total = props.contentWidth
  const visible = props.viewportWidth
  if (trackPx <= 0 || total <= visible) {
    emit('update:scrollLeft', 0)
    return
  }
  const ratio = targetLeftPx / (trackPx * (1 - visible / total))
  const scroll = Math.max(
    0,
    Math.min(total - visible, ratio * (total - visible))
  )
  emit('update:scrollLeft', scroll)
}

const onTrackPointerDown = (e: PointerEvent) => {
  const el = trackRef.value
  if (!el) return
  isDragging.value = 'track'
  const rect = el.getBoundingClientRect()
  const winPx = (props.viewportWidth / props.contentWidth) * rect.width
  scrollFromPointer(e.clientX, winPx / 2)
  attachMove(winPx / 2)
}

const onWindowPointerDown = (e: PointerEvent) => {
  const el = trackRef.value
  if (!el) return
  isDragging.value = 'window'
  const rect = el.getBoundingClientRect()
  const winPx = (props.viewportWidth / props.contentWidth) * rect.width
  const winLeftPx =
    (props.scrollLeft / (props.contentWidth - props.viewportWidth || 1)) *
    (rect.width - winPx)
  dragOffset = e.clientX - rect.left - winLeftPx
  attachMove(dragOffset)
}

const attachMove = (offset: number) => {
  dragOffset = offset
  const onMove = (ev: PointerEvent) => {
    scrollFromPointer(ev.clientX, dragOffset)
  }
  const onUp = () => {
    isDragging.value = null
    window.removeEventListener('pointermove', onMove)
    window.removeEventListener('pointerup', onUp)
    window.removeEventListener('pointercancel', onUp)
  }
  window.addEventListener('pointermove', onMove)
  window.addEventListener('pointerup', onUp)
  window.addEventListener('pointercancel', onUp)
}

onUnmounted(() => {
  isDragging.value = null
})
</script>
